// /onboard#t=<token> → exchange the token for a session cookie, then strip it from the URL.
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { IntakeState, SessionResponse } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { Button, Spinner } from '../ui';
import { IntakeProvider } from '../intake/IntakeContext';
import { FirmMessage } from './Message';

const LINK_TOKEN_KEY = 'upm.linkToken';

/**
 * Token from the link's fragment (/onboard#t=…), with an optional landing hint `&to=status` (agreement emails
 * open the status page). Fragments never reach the server or its logs.
 */
function parseHash(hash: string): { token: string; to: 'status' | null } | null {
  const m = /^#t=([A-Za-z0-9_-]{20,200})(?:&to=(status))?$/.exec(hash);
  return m ? { token: m[1]!, to: m[2] === 'status' ? 'status' : null } : null;
}

/** /onboard: exchange a fragment token first; otherwise render the firm routes. */
export function OnboardGate() {
  // useLocation subscribes this gate to navigation, so it re-renders once the exchange navigates away.
  const { hash } = useLocation();
  const parsed = parseHash(hash);
  return parsed ? <TokenExchange token={parsed.token} to={parsed.to} /> : <Outlet />;
}

export function TokenExchange({ token, to = null }: { token: string; to?: 'status' | null }) {
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Take the token out of the address bar and history right away.
    window.history.replaceState(window.history.state, '', '/onboard');
    (async () => {
      try {
        const res = await api.post<SessionResponse>('/api/intake/session', { token });
        try { sessionStorage.removeItem(LINK_TOKEN_KEY); } catch { /* ignore */ }
        navigate(res.view === 'status' || to === 'status' ? '/onboard/status' : '/onboard', { replace: true });
      } catch (e) {
        if (e instanceof ApiError && ['link_expired', 'link_revoked', 'link_invalid'].includes(e.code)) {
          try { sessionStorage.setItem(LINK_TOKEN_KEY, token); } catch { /* ignore */ }
          navigate('/onboard/link', { replace: true, state: { code: e.code } });
        } else {
          setError(e instanceof ApiError && e.code === 'rate_limited' ? 'Too many attempts. Wait a minute and open the link again.' : 'We couldn’t open your link. Check your connection and open it again.');
        }
      }
    })();
  }, [token, to, navigate]);

  if (error) return <FirmMessage title="Something went wrong">{<p className="m-0">{error}</p>}</FirmMessage>;
  return <div className="min-h-screen bg-page"><Spinner label="Opening your onboarding" /></div>;
}

/** Expired, revoked or unknown link: offer to notify the Customer Experience Manager. Never shows firm data. */
const PROBLEM: Record<string, { title: string; body: string }> = {
  link_expired: { title: 'This link has expired', body: 'For your security, onboarding links stop working after 30 days.' },
  link_revoked: { title: 'This link has been replaced', body: 'A newer link was sent for your firm, so this one no longer works.' },
  link_invalid: { title: 'This link isn’t valid', body: 'The link may be incomplete. Check that you opened the whole link from your invitation email.' },
};

export function LinkProblem() {
  const loc = useLocation();
  const code = (loc.state as { code?: string } | null)?.code ?? 'link_expired';
  const copy = PROBLEM[code] ?? PROBLEM.link_expired!;
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  let token: string | null = null;
  try { token = sessionStorage.getItem(LINK_TOKEN_KEY); } catch { token = null; }

  const request = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await api.post('/api/intake/link-request', { token });
      setSent(true);
      try { sessionStorage.removeItem(LINK_TOKEN_KEY); } catch { /* ignore */ }
    } catch (e) {
      // The confirmation is the same whatever the server knows about the link.
      if (e instanceof ApiError && e.code !== 'network' && e.status < 500) setSent(true); else setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <FirmMessage title="We’ve let your Customer Experience Manager know">
        <p className="m-0">A new link is on its way to the email address that received your invitation. It usually arrives within one business day.</p>
        <p className="m-0">Your saved answers are kept. The new link picks up where you left off.</p>
      </FirmMessage>
    );
  }
  return (
    <FirmMessage title={copy.title}>
      <p className="m-0">{copy.body}</p>
      <p className="m-0">Request a new link and we’ll send it to the email address that received your invitation. Your saved answers are kept.</p>
      {failed && <p role="alert" className="m-0 font-semibold text-bad">We couldn’t send the request. Try again.</p>}
      <div><Button variant="primary" size="lg" onClick={() => void request()} disabled={busy || !token}>Request a new link</Button></div>
      {!token && <p className="m-0 text-sm text-muted">Open the link from your invitation email again to request a new one.</p>}
    </FirmMessage>
  );
}

export function NoSession() {
  return (
    <FirmMessage title="Open the link from your invitation email">
      <p className="m-0">Your onboarding opens from the personal link in the invitation email we sent you. Open that email and select the link to continue where you left off.</p>
      <p className="m-0">Can’t find it, or the link has expired? Reply to the invitation email and we’ll send a new one.</p>
    </FirmMessage>
  );
}

/** Loads the intake once for /onboard, /onboard/book and /onboard/done. */
export function FirmRoot() {
  const [state, setState] = useState<IntakeState | null>(null);
  const [status, setStatus] = useState<'loading' | 'nosession' | 'error' | 'ok'>('loading');
  useEffect(() => {
    api.get<IntakeState>('/api/intake').then(
      (s) => { setState(s); setStatus('ok'); },
      (e) => setStatus(e instanceof ApiError && (e.status === 401 || e.code === 'unauthorized') ? 'nosession' : 'error'),
    );
  }, []);
  if (status === 'nosession') return <NoSession />;
  if (status === 'error') return <FirmMessage title="Something went wrong"><p className="m-0">We couldn’t load your onboarding. Refresh the page to try again.</p></FirmMessage>;
  if (!state) return <div className="min-h-screen bg-page"><Spinner label="Loading your onboarding" /></div>;
  return (
    <IntakeProvider initial={state}>
      <Outlet />
    </IntakeProvider>
  );
}
